import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { produce } from 'immer'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { handlePaginationParamsAfterDelete } from '@/helpers/api'

import {
  bookmarkCheckQueryKeys,
  bookmarksQueryKeys,
} from '@/hooks/bookmarks/types'
import { useHandleError } from '@/hooks/common/useErrorHandler'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { Bookmark, BookmarkCheck } from '@/types/bookmark/Bookmark'
import type { UUID } from '@/types/Id'

interface UseDeleteBookmarkProps {
  orgId: UUID | ''
  optimistic?: boolean
}

interface DeleteBookmarkRequest {
  bookmarkId: UUID
}

const deleteBookmark = async (
  orgId: UUID | '',
  bookmarkId: UUID,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    throw new Error('No access token or org ID provided.')
  }

  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/bookmarks/${bookmarkId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new Error(res.statusText)
  }
}

export const useDeleteBookmark = ({
  orgId,
  optimistic = true,
}: UseDeleteBookmarkProps) => {
  const { accessToken } = useAuthInfo()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    DeleteBookmarkRequest,
    InfiniteData<PaginatedResponse<Bookmark>, PaginateRequest> | undefined
  >({
    mutationFn: ({ bookmarkId }) =>
      deleteBookmark(orgId, bookmarkId, accessToken),
    onMutate: async ({ bookmarkId }) => {
      const key = bookmarksQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key })

      const previousData =
        queryClient.getQueryData<
          InfiniteData<PaginatedResponse<Bookmark>, PaginateRequest>
        >(key)

      if (previousData && optimistic) {
        // Find the bookmark to get its type and object_id for cache clearing
        const bookmark = previousData.pages
          .flatMap((page) => page.data)
          .find((b) => b.id === bookmarkId)

        // Clear the check cache for this bookmark
        if (bookmark) {
          const checkKey = bookmarkCheckQueryKeys.check({
            orgId,
            bookmarkableType: bookmark.bookmarkable_type,
            objectId: bookmark.object_id,
          })
          await queryClient.cancelQueries({ queryKey: checkKey })
          const checkData: BookmarkCheck = {
            is_bookmarked: false,
            bookmark_id: null,
          }
          queryClient.setQueryData(checkKey, checkData)
        }

        queryClient.setQueryData<InfiniteData<PaginatedResponse<Bookmark>>>(
          key,
          produce(previousData, (draft) => {
            draft.pages = draft.pages.map((page) => {
              page.data = page.data.filter((b) => b.id !== bookmarkId)
              return page
            })

            draft.pageParams = handlePaginationParamsAfterDelete(
              [bookmarkId],
              previousData,
            )
          }),
        )
      }

      return previousData
    },
    onError: (err, _req, context) => {
      const key = bookmarksQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSettled: () => {
      const key = bookmarksQueryKeys.organization({ orgId })
      return queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
